import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDownIcon, StarIcon, BeakerIcon } from "@heroicons/react/24/outline";
import { RecipeType, recipeTypeToPath } from "@/lib/types";

const OPEN_DELAY_MS = 120;
const CLOSE_DELAY_MS = 200;

export const RecipesMenu = () => {
    const { pathname } = useLocation();
    const isActive = pathname.startsWith("/recipes") && !pathname.startsWith("/recipes/new");
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const openTimer = useRef<number | null>(null);
    const closeTimer = useRef<number | null>(null);

    const cancelTimers = () => {
        if (openTimer.current) {
            window.clearTimeout(openTimer.current);
            openTimer.current = null;
        }
        if (closeTimer.current) {
            window.clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    };

    const scheduleOpen = () => {
        cancelTimers();
        openTimer.current = window.setTimeout(() => setIsOpen(true), OPEN_DELAY_MS);
    };

    const scheduleClose = () => {
        cancelTimers();
        closeTimer.current = window.setTimeout(() => setIsOpen(false), CLOSE_DELAY_MS);
    };

    const closeNow = useCallback(() => {
        if (openTimer.current) {
            window.clearTimeout(openTimer.current);
            openTimer.current = null;
        }
        if (closeTimer.current) {
            window.clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
        setIsOpen(false);
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") closeNow();
        };
        const onPointerDown = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                closeNow();
            }
        };
        document.addEventListener("keydown", onKey);
        document.addEventListener("mousedown", onPointerDown);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.removeEventListener("mousedown", onPointerDown);
        };
    }, [isOpen, closeNow]);

    useEffect(() => () => cancelTimers(), []);

    return (
        <div
            ref={containerRef}
            className="relative"
            onMouseEnter={scheduleOpen}
            onMouseLeave={scheduleClose}
            onFocusCapture={() => setIsOpen(true)}
            onBlurCapture={(e) => {
                if (!containerRef.current?.contains(e.relatedTarget as Node | null)) {
                    closeNow();
                }
            }}
        >
            <Link
                to="/recipes"
                aria-current={isActive ? "page" : undefined}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                className="inline-flex items-center gap-1 px-2 py-1.5 font-EBGaramond text-lg text-default-700 hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 focus-visible:rounded-md"
                onClick={closeNow}
            >
                Recipes
                <ChevronDownIcon
                    aria-hidden="true"
                    className={`w-4 h-4 transition-transform duration-150 ${isOpen ? "rotate-180" : ""}`}
                />
            </Link>

            <AnimatePresence>
                {isOpen ? (
                    <motion.div
                        role="menu"
                        aria-label="Recipes"
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.15, ease: [0, 0, 0.2, 1] }}
                        className="absolute top-full left-1/2 -translate-x-1/2 mt-3 z-50"
                    >
                        <MegaMenu onItemClick={closeNow} />
                    </motion.div>
                ) : null}
            </AnimatePresence>
        </div>
    );
};

interface MegaMenuProps {
    onItemClick: () => void;
}

const MegaMenu = ({ onItemClick }: MegaMenuProps) => {
    return (
        <div className="relative w-[280px] overflow-hidden rounded-2xl bg-[var(--background)]/95 backdrop-blur-md backdrop-saturate-150 border border-accent-500/20 shadow-[0_12px_40px_rgba(20,48,42,0.18)]">
            {/* Decorative watermarks intentionally absent — see MobileMenu.tsx and .spec/nav-redesign/design.md §13 */}
            <div className="relative py-3 flex flex-col">
                <MegaItem to="/recipes?isFavorite=true" onClick={onItemClick} icon={<StarIcon className="w-4 h-4" />}>
                    Favorites
                </MegaItem>
                <MegaItem to="/recipes?hasMadeIt=false" onClick={onItemClick} icon={<BeakerIcon className="w-4 h-4" />}>
                    Not yet tried
                </MegaItem>

                <div className="my-2 mx-5 h-px bg-gradient-to-r from-transparent via-accent-500/30 to-transparent" />

                {Object.values(RecipeType).map((type) => (
                    <MegaItem
                        key={type}
                        to={`/recipes/type/${recipeTypeToPath(type)}`}
                        onClick={onItemClick}
                    >
                        {type}
                    </MegaItem>
                ))}
            </div>
        </div>
    );
};

interface MegaItemProps {
    to: string;
    onClick: () => void;
    children: React.ReactNode;
    icon?: React.ReactNode;
}

const MegaItem = ({ to, onClick, children, icon }: MegaItemProps) => (
    <Link
        to={to}
        onClick={onClick}
        role="menuitem"
        className="font-EBGaramond text-lg text-default-700 hover:text-secondary-900 hover:bg-secondary-100 px-6 py-2.5 transition-colors inline-flex items-center gap-2.5"
    >
        {icon ? <span className="text-default-500 shrink-0">{icon}</span> : null}
        <span>{children}</span>
    </Link>
);
